import { useState, useEffect } from "react";
import axios from "axios";
import API_ENDPOINTS from "config/api";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Avatar from "@mui/material/Avatar";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DataTable from "examples/Tables/DataTable";

export default function Conductores() {
  const [conductores, setConductores] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editando, setEditando] = useState(false);
  const [conductorActual, setConductorActual] = useState({
    nombres: "",
    apellidos: "",
    documento: "",
    tipo_licencia: "",
    numero_licencia: "",
    vencimiento_licencia: "",
    telefono: "",
    email: "",
    direccion: "",
    fecha_ingreso: "",
    estado: "activo",
  });

  const tiposLicencia = ["A1", "A2", "B1", "B2", "C1", "C2"];
  const estadosConductor = [
    { value: "activo", label: "Activo", color: "success" },
    { value: "vacaciones", label: "Vacaciones", color: "info" },
    { value: "licencia", label: "Licencia Médica", color: "warning" },
    { value: "inactivo", label: "Inactivo", color: "secondary" },
  ];

  useEffect(() => {
    cargarConductores();
  }, []);

  const cargarConductores = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.LOGISTICA.CONDUCTORES);
      setConductores(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error al cargar conductores:", error);
      setConductores([]);
    }
  };

  const handleOpenDialog = (conductor = null) => {
    if (conductor) {
      setEditando(true);
      setConductorActual(conductor);
    } else {
      setEditando(false);
      setConductorActual({
        nombres: "",
        apellidos: "",
        documento: "",
        tipo_licencia: "",
        numero_licencia: "",
        vencimiento_licencia: "",
        telefono: "",
        email: "",
        direccion: "",
        fecha_ingreso: "",
        estado: "activo",
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditando(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setConductorActual({ ...conductorActual, [name]: value });
  };

  const handleGuardar = async () => {
    try {
      if (editando) {
        await axios.put(
          `${API_ENDPOINTS.LOGISTICA.CONDUCTORES}/${conductorActual.id}`,
          conductorActual
        );
      } else {
        await axios.post(API_ENDPOINTS.LOGISTICA.CONDUCTORES, conductorActual);
      }
      handleCloseDialog();
      cargarConductores();
    } catch (error) {
      console.error("Error al guardar conductor:", error);
    }
  };

  const handleEliminar = async (id) => {
    if (window.confirm("¿Está seguro de eliminar este conductor?")) {
      try {
        await axios.delete(`${API_ENDPOINTS.LOGISTICA.CONDUCTORES}/${id}`);
        cargarConductores();
      } catch (error) {
        console.error("Error al eliminar conductor:", error);
      }
    }
  };

  const getEstadoChip = (estado) => {
    const est = estadosConductor.find((e) => e.value === estado) || estadosConductor[0];
    return <Chip label={est.label} color={est.color} size="small" />;
  };

  /* eslint-disable react/prop-types */
  const columns = [
    {
      Header: "Conductor",
      accessor: "conductor",
      width: "20%",
      Cell: ({ row }) => (
        <MDBox display="flex" alignItems="center" gap={2}>
          <Avatar sx={{ bgcolor: "info.main" }}>
            {row.original.nombres?.charAt(0) || "C"}
            {row.original.apellidos?.charAt(0) || ""}
          </Avatar>
          <MDBox>
            <MDTypography variant="button" fontWeight="bold">
              {row.original.nombres} {row.original.apellidos}
            </MDTypography>
            <MDTypography variant="caption" color="text" display="block">
              Doc: {row.original.documento}
            </MDTypography>
          </MDBox>
        </MDBox>
      ),
    },
    { Header: "Licencia", accessor: "tipo_licencia", width: "8%" },
    { Header: "Teléfono", accessor: "telefono", width: "12%" },
    { Header: "Email", accessor: "email", width: "15%" },
    { Header: "Vehículo", accessor: "vehiculo_asignado", width: "10%" },
    {
      Header: "Rutas",
      accessor: "rutas_completadas",
      width: "8%",
      Cell: ({ value }) => (
        <MDBox textAlign="center">
          <MDTypography variant="button" fontWeight="bold" color="info">
            {value}
          </MDTypography>
        </MDBox>
      ),
    },
    {
      Header: "Calificación",
      accessor: "calificacion",
      width: "10%",
      Cell: ({ value }) => (
        <MDBox display="flex" alignItems="center" gap={0.5}>
          <Icon fontSize="small" color="warning">
            star
          </Icon>
          <MDTypography variant="button" fontWeight="medium">
            {value}
          </MDTypography>
        </MDBox>
      ),
    },
    {
      Header: "Estado",
      accessor: "estado",
      width: "10%",
      Cell: ({ value }) => getEstadoChip(value),
    },
    {
      Header: "Acciones",
      accessor: "acciones",
      width: "7%",
      Cell: ({ row }) => (
        <MDBox display="flex" gap={1}>
          <Tooltip title="Editar">
            <IconButton size="small" color="info" onClick={() => handleOpenDialog(row.original)}>
              <Icon fontSize="small">edit</Icon>
            </IconButton>
          </Tooltip>
          <Tooltip title="Historial">
            <IconButton size="small" color="primary">
              <Icon fontSize="small">history</Icon>
            </IconButton>
          </Tooltip>
          <Tooltip title="Eliminar">
            <IconButton size="small" color="error" onClick={() => handleEliminar(row.original.id)}>
              <Icon fontSize="small">delete</Icon>
            </IconButton>
          </Tooltip>
        </MDBox>
      ),
    },
  ];
  /* eslint-enable react/prop-types */

  return (
    <MDBox>
      <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <MDTypography variant="h5">Conductores</MDTypography>
        <MDButton
          variant="gradient"
          color="info"
          startIcon={<Icon>add</Icon>}
          onClick={() => handleOpenDialog()}
        >
          Nuevo Conductor
        </MDButton>
      </MDBox>

      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} md={3}>
          <Card>
            <MDBox p={2} textAlign="center">
              <Icon fontSize="large" color="info">
                badge
              </Icon>
              <MDTypography variant="h4" fontWeight="bold" mt={1}>
                {conductores.length}
              </MDTypography>
              <MDTypography variant="caption" color="text">
                Total Conductores
              </MDTypography>
            </MDBox>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <MDBox p={2} textAlign="center">
              <Icon fontSize="large" color="success">
                check_circle
              </Icon>
              <MDTypography variant="h4" fontWeight="bold" mt={1}>
                {conductores.filter((c) => c.estado === "activo").length}
              </MDTypography>
              <MDTypography variant="caption" color="text">
                Activos
              </MDTypography>
            </MDBox>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <MDBox p={2} textAlign="center">
              <Icon fontSize="large" color="warning">
                star
              </Icon>
              <MDTypography variant="h4" fontWeight="bold" mt={1}>
                {conductores.length > 0
                  ? (
                    conductores.reduce((sum, c) => sum + c.calificacion, 0) / conductores.length
                  ).toFixed(1)
                  : "0.0"}
              </MDTypography>
              <MDTypography variant="caption" color="text">
                Calificación Promedio
              </MDTypography>
            </MDBox>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <MDBox p={2} textAlign="center">
              <Icon fontSize="large" color="primary">
                local_shipping
              </Icon>
              <MDTypography variant="h4" fontWeight="bold" mt={1}>
                {conductores.reduce((sum, c) => sum + c.rutas_completadas, 0)}
              </MDTypography>
              <MDTypography variant="caption" color="text">
                Rutas Totales
              </MDTypography>
            </MDBox>
          </Card>
        </Grid>
      </Grid>

      <Card>
        <DataTable
          table={{ columns, rows: conductores }}
          showTotalEntries={true}
          isSorted={true}
          noEndBorder
          entriesPerPage={false}
          canSearch
        />
      </Card>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editando ? "Editar Conductor" : "Nuevo Conductor"}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} mt={1}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nombres"
                name="nombres"
                value={conductorActual.nombres}
                onChange={handleInputChange}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Apellidos"
                name="apellidos"
                value={conductorActual.apellidos}
                onChange={handleInputChange}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Documento de Identidad"
                name="documento"
                value={conductorActual.documento}
                onChange={handleInputChange}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Tipo de Licencia"
                name="tipo_licencia"
                value={conductorActual.tipo_licencia}
                onChange={handleInputChange}
                required
              >
                {tiposLicencia.map((tipo) => (
                  <MenuItem key={tipo} value={tipo}>
                    Licencia {tipo}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Número de Licencia"
                name="numero_licencia"
                value={conductorActual.numero_licencia}
                onChange={handleInputChange}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="date"
                label="Vencimiento de Licencia"
                name="vencimiento_licencia"
                value={conductorActual.vencimiento_licencia}
                onChange={handleInputChange}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Teléfono"
                name="telefono"
                value={conductorActual.telefono}
                onChange={handleInputChange}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="email"
                label="Email"
                name="email"
                value={conductorActual.email}
                onChange={handleInputChange}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Dirección"
                name="direccion"
                value={conductorActual.direccion}
                onChange={handleInputChange}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="date"
                label="Fecha de Ingreso"
                name="fecha_ingreso"
                value={conductorActual.fecha_ingreso}
                onChange={handleInputChange}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                select
                label="Estado"
                name="estado"
                value={conductorActual.estado}
                onChange={handleInputChange}
                required
              >
                {estadosConductor.map((estado) => (
                  <MenuItem key={estado.value} value={estado.value}>
                    {estado.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button onClick={handleGuardar} variant="contained" color="info">
            {editando ? "Actualizar" : "Crear"}
          </Button>
        </DialogActions>
      </Dialog>
    </MDBox>
  );
}
